import { CdkVirtualScrollableElement, VIRTUAL_SCROLLABLE } from '@angular/cdk/scrolling';
import {
  Component,
  ElementRef,
  EventEmitter,
  InjectionToken,
  Injector,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SidePanelModel } from '@assetaccountant/assets/models/side-panel-model';
import { Unsubscribable } from 'rxjs';

export const ActiveSidePanel = new InjectionToken<ActiveSidePanel>('ACTIVE_SIDE_PANEL');
export interface ActiveSidePanel {
  close(): void;
  disableClose(): void;
  enableClose(): void;
  setWidth(width: 'sm' | 'md' | 'lg' | 'xl' | undefined): void;
}

@Component({
  selector: 'app-side-panel',
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  standalone: false,
})
export class SidePanelComponent {
  @Output() panelClosed = new EventEmitter<null>();

  @ViewChild('sidePanel', { read: ViewContainerRef }) sidePanelHost: ViewContainerRef;
  @ViewChild(CdkVirtualScrollableElement) virtualScrollable: CdkVirtualScrollableElement;

  closeDisabled = false;
  heading = '';
  isOpen = false;
  width?: 'sm' | 'md' | 'lg' | 'xl';

  private subscriptions: Unsubscribable | Unsubscribable[];

  constructor(
    private el: ElementRef<HTMLElement>,
    private injector: Injector,
  ) {}

  close() {
    if (this.closeDisabled) {
      return;
    }

    this.isOpen = false;
    this.panelClosed.emit();

    if (this.subscriptions) {
      if ('length' in this.subscriptions) {
        this.subscriptions.forEach(s => s.unsubscribe());
      } else {
        this.subscriptions.unsubscribe();
      }
    }

    const viewContainerRef = this.sidePanelHost;
    this.onceTransitionEnds(() => {
      viewContainerRef.clear();
    });
  }

  disableClose() {
    this.closeDisabled = true;
  }

  enableClose() {
    this.closeDisabled = false;
  }

  onEscapeKey() {
    this.close();
  }

  async open<T>(
    newPanel: SidePanelModel<T>,
    setInputs?: (component: T) => void,
    subscribe?: (component: T) => Unsubscribable | Unsubscribable[],
  ) {
    if (this.isOpen) {
      this.isOpen = false;
      await new Promise(r => {
        this.onceTransitionEnds(() => r(null));
      });
    }

    this.sidePanelHost.clear();

    if (newPanel.heading != null) {
      this.heading = newPanel.heading || '';
    }
    this.width = newPanel.options.width;

    const componentInjector = Injector.create({
      providers: [
        {
          provide: ActiveSidePanel,
          useValue: {
            close: () => this.close(),
            disableClose: () => this.disableClose(),
            enableClose: () => this.enableClose(),
            setWidth: (w: 'sm' | 'md' | 'lg' | 'xl' | undefined) => (this.width = w),
          },
        },
        {
          provide: VIRTUAL_SCROLLABLE,
          useValue: this.virtualScrollable,
        },
      ],
      parent: this.injector,
    });

    const componentRef = this.sidePanelHost.createComponent(newPanel.component, {
      injector: componentInjector,
    });

    if (setInputs) {
      setInputs(componentRef.instance);
    }

    if (subscribe) {
      this.subscriptions = subscribe(componentRef.instance);
    }

    this.isOpen = true;

    // Focus the close button
    const closeButton = this.el.nativeElement.querySelector('.btn-close') as HTMLButtonElement;
    this.onceTransitionEnds(() => {
      closeButton.focus();
    });
  }

  onceTransitionEnds(callback: () => void) {
    this.el.nativeElement.ontransitionend = ev => {
      if (
        (ev.target as HTMLElement).classList.contains('side-panel') &&
        ev.propertyName === 'transform'
      ) {
        this.el.nativeElement.ontransitionend = null;
        callback();
      }
    };
  }
}
